/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { getDateLabel, getWeekDayLabel } from './openingTimeHelpers'
import { format } from 'date-fns'

const InsightElement = styled.div`
    display: inline-flex;
    flex-direction: column;
    text-align: center;
`

const InsightLabel = styled.div`
    font-size: .8em;
    text-transform: uppercase;
`

const InsightValue = styled.div`
    font-weight: bold;
    font-size: 1.1rem;
`

const findExtremeOpeningDay = (openings, comparator) => {
    if (openings.length === 0) return null

    return openings.reduce((extremeDay, current) => comparator(current.averageOpenRate, extremeDay.averageOpenRate) ?
        current :
        extremeDay,
      openings[0],
    )
}

const findMostClosedHour = (openings, isWeekly) => {
    const hours = {}
    for (const day of openings) {
        for (const h of day.hours || []) {
            const hour = h.hour.padStart(2, '0')
            const closed = (h.offTimeInMinutes || 0) + (h.pausedTimeInMinutes || 0)
            const base = isWeekly ? 60 : (h.regularOpeningTimeInMinutes || 0)
            if (!hours[hour]) hours[hour] = {closedTotal: 0, baseTotal: 0}
            hours[hour].closedTotal += closed
            hours[hour].baseTotal += base
        }
    }

    const entries = Object.entries(hours)
    if (entries.length === 0) return null

    const computed = entries.map(([hour, data]) => {
        const rawClosedAvg = data.baseTotal > 0 ? (data.closedTotal / data.baseTotal) * 100 : 100
        const closedAvg = Number(rawClosedAvg.toFixed(3)) // round to avoid tiny floating diffs
        const openPercent = Math.round(100 - Math.min(100, Math.max(0, closedAvg)))
        return {hour, closedAvg, openPercent}
    })

    if (computed.length === 0) return null

    const maxClosedAvg = Math.max(...computed.map(c => c.closedAvg))
    const topEqual = computed
        .filter(c => c.closedAvg === maxClosedAvg)
        .sort((a, b) => a.hour.localeCompare(b.hour))
        .slice(0, 3)

    if (topEqual.length === 0) return null

    return topEqual.map(item => ({label: `${item.hour}h - ${item.hour}h59`, rate: item.openPercent}))
}

const Insights = ({openings, isWeekly, peakTimePeriods, ...props}) => {
    const [t] = useTranslation()

    const today = format(new Date(), 'yyyy-MM-dd')
    const openingsForExtremes = isWeekly ? openings : openings.filter(day => day.date !== today)

    const mostOpenDay = findExtremeOpeningDay(openingsForExtremes, (a, b) => a > b)
    const mostClosedDay = findExtremeOpeningDay(openingsForExtremes, (a, b) => a < b)
    const mostClosedHour = findMostClosedHour(openings, isWeekly)

    return (
      <div
        className={props.className}
        css={css`
            display: flex;
            justify-content: space-evenly;
            flex-wrap: wrap;
        `
        }>
          {peakTimePeriods?.length > 0 &&
            <InsightElement>
                <InsightLabel>{t('openings.opening_times.insights.peakTimeSlotsLabel', {count: peakTimePeriods.length})}</InsightLabel>
                <InsightValue>
                    {peakTimePeriods.map(peakTimePeriod => {
                          const value = `${peakTimePeriod.start_time} - ${peakTimePeriod.end_time}`
                          return (
                            <div key={value}>
                                {value}
                            </div>
                          )
                      },
                    )}
                </InsightValue>
            </InsightElement>
          }

          {mostOpenDay &&
            <InsightElement>
                <InsightLabel>{t('openings.opening_times.insights.mostOpenDayLabel')}</InsightLabel>
                <InsightValue>
                    {t('openings.opening_times.insights.value_rate', {
                        value: isWeekly ? t(`days.${getWeekDayLabel(mostOpenDay)}`) : getDateLabel(mostOpenDay),
                        rate: mostOpenDay.averageOpenRate,
                    })}
                </InsightValue>
            </InsightElement>
          }

          {mostClosedDay &&
            <InsightElement>
                <InsightLabel>{t('openings.opening_times.insights.mostClosedDayLabel')}</InsightLabel>
                <InsightValue>
                    {mostClosedDay.averageOpenRate === 100 ?
                      '-' :
                      t('openings.opening_times.insights.value_rate', {
                          value: isWeekly ? t(`days.${getWeekDayLabel(mostClosedDay)}`) : getDateLabel(mostClosedDay),
                          rate: mostClosedDay.averageOpenRate,
                      })}
                </InsightValue>
            </InsightElement>
          }

          {mostClosedHour &&
            <InsightElement>
                <InsightLabel>{t('openings.opening_times.insights.mostClosedHourLabel', { count: mostClosedHour.length })}</InsightLabel>
                <InsightValue>
                    {Array.isArray(mostClosedHour) ? (
                      // If all returned items are fully open (rate 100) show '-', else render each
                      mostClosedHour.every(h => h.rate === 100) ? '-' : mostClosedHour.map(h => (
                        <div key={h.label}>
                            {t('openings.opening_times.insights.value_rate', {
                                value: h.label,
                                rate: h.rate,
                            })}
                        </div>
                      ))
                    ) : (
                      mostClosedHour.rate === 100 ? '-' :
                        t('openings.opening_times.insights.value_rate', {
                            value: mostClosedHour.label,
                            rate: mostClosedHour.rate,
                        })
                    )}
                </InsightValue>
            </InsightElement>
          }
      </div>
    )
}

export { Insights }
